import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Poppins, Spacer } from 'src/common';
import { FormikProvider, useFormik } from 'formik';
import _ from 'lodash';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import { BottomPanel, Slide, Stepper, StyledModal } from '../comps';
import { Guard } from 'src/components/modals';
import { useStateSelector } from 'src/redux';
import { motion } from 'framer-motion';
import { WizardRecomendedControlsIntent } from 'src/redux/reducers/app/types';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { RecomendedControlsSlideId } from 'src/api/types/wizard';
import { addControlsSlides, addScenariosSlides } from './slides';
import { SlideConfig } from './types';
import { initialValues, SLIDE_HEIGHT, hasNewSelectedControls, getControlScenarioGroups } from './util';
import { wizCreateScenarios } from 'src/api/wizard';
import { QKeys } from 'src/api/types';
import { queryCache } from 'react-query';
import { useCache } from 'src/graphql';
import {
  FrameworkControlScenarioInput,
  useAddControlsFromLibraryMutation,
  useControlsRmpQuery,
  useLinkFrameworkControlsToScenariosMutation,
} from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';
import { ModalSubTitle } from 'src/components/modals/comps';
import { showSuccessToast, toastMessages } from 'src/components/Toast';

interface ModalProps {
  onClose: () => void;
}

export const Modal: React.FC<ModalProps> = ({ onClose }) => {
  const wizardRecomendedControls = useStateSelector(({ app }) => app.wizardRecomendedControls);
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [step, setStep] = useState(0);
  const [slides, setSlides] = useState<SlideConfig[]>([]);
  const [canRender, setCanRender] = useState(false);
  const { refetchQuery } = useCache();
  const { workspaceId } = useAuth();

  const isLastSlide = step === slides.length - 1;
  const isInitialRender = useRef(true);

  const isLinkControls = wizardRecomendedControls?.intent.type === WizardRecomendedControlsIntent.linkControls;
  const { data: { controls = [] } = {} } = useControlsRmpQuery({
    variables: { workspaceId: workspaceId },
    skip: !isLinkControls,
  });

  const [linkFrameworkControlsToScenarios] = useLinkFrameworkControlsToScenariosMutation();

  const [addControlsFromLibrary] = useAddControlsFromLibraryMutation();

  const [sliderRef, slider] = useKeenSlider({
    slideChanged: (slider) => {
      setStep(slider.track.details.abs);
    },
    drag: false,
  });

  const onChangeSlide = (id: RecomendedControlsSlideId) => {
    const idx = slides.findIndex((el) => el.id === id);
    slider.current?.moveToIdx(idx, true, { duration: 0 });
  };

  useEffect(() => {
    setError(null);
    validateForm();
    if (!isCloseRequested && isInitialRender.current) {
      setTimeout(() => {
        slider.current?.moveToIdx(step, true, { duration: 0 });
        isInitialRender.current = false;
      }, 0);
    }
  }, [step, isCloseRequested]);

  useEffect(() => {
    if (wizardRecomendedControls?.intent.type === WizardRecomendedControlsIntent.addScenarios) {
      setSlides(addScenariosSlides);
      onChangeSlide(RecomendedControlsSlideId.scenarios);
    }

    if (wizardRecomendedControls?.intent.type === WizardRecomendedControlsIntent.linkControls) {
      setSlides(addControlsSlides);
      onChangeSlide(RecomendedControlsSlideId.controls);
    }

    setCanRender(true);
    setTimeout(() => {
      isInitialRender.current = false;
    }, 0);
  }, [wizardRecomendedControls]);

  const formik = useFormik({
    initialValues,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (val) => {
      setError(null);

      try {
        if (wizardRecomendedControls?.intent.type === WizardRecomendedControlsIntent.addScenarios) {
          const assessmentId = wizardRecomendedControls.intent.params.assessmentId;

          return wizCreateScenarios({ data: val, id: assessmentId }).then(async () => {
            queryCache.invalidateQueries([QKeys.RankedControlsAssessment]);
            queryCache.invalidateQueries([QKeys.Audit]);
            await refetchQuery({ include: ['assessment'] });
            showSuccessToast(toastMessages.scenario.created);
            onClose();
          });
        }

        if (wizardRecomendedControls?.intent.type === WizardRecomendedControlsIntent.linkControls) {
          const frameworkControlScenarioLinks: FrameworkControlScenarioInput[] = [];

          for (const [controlId, scenarios] of Object.entries(val.selectedControls)) {
            for (const [scenarioId, isSelected] of Object.entries(scenarios)) {
              if (isSelected) {
                frameworkControlScenarioLinks.push({
                  frameworkControlId: controlId,
                  scenarioId: scenarioId,
                });
              }
            }
          }

          const { existingRmpScenarioGroups, newControlScenarioGroups } = getControlScenarioGroups({
            controls,
            frameworkControlScenarioLinks,
            selectedControls: val.selectedControls,
          });

          const existingPromises = existingRmpScenarioGroups.map((group) =>
            linkFrameworkControlsToScenarios({
              variables: {
                frameworkControlScenarioLinks: group.scenarioIds.map((scenarioId) => ({
                  frameworkControlId: group.controlId,
                  scenarioId,
                })),
                addRecommendedModifiers: val.addRecommendedModifiers,
              },
            }),
          );

          const newControlPromises = newControlScenarioGroups.map((group) =>
            addControlsFromLibrary({
              variables: {
                controlIds: group.scenarioIds.map((scenarioId) => ({
                  id: group.controlId,
                  scenarioId,
                })),
                workspaceId,
                addRecommendedModifiers: val.addRecommendedModifiers,
              },
            }),
          );

          await Promise.all([...existingPromises, ...newControlPromises]);
          queryCache.invalidateQueries([QKeys.RankedControlsAssessment]);
          queryCache.invalidateQueries([QKeys.Audit]);
          await refetchQuery({ include: ['assessment', 'controlsRmp'] });
          showSuccessToast(toastMessages.control.linked);
          onClose();
        }
      } catch (error: any) {
        setError(error);
      }
    },
  });

  const { dirty, isSubmitting, handleSubmit, values, validateForm } = formik;

  const hasNewSelected = useMemo(
    () => hasNewSelectedControls(values.selectedControls, controls),
    [values.selectedControls, controls],
  );

  if (!canRender) return null;

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={dirty}
      modalComponent={
        <StyledModal
          $slideHeight={SLIDE_HEIGHT}
          $width={1400}
          onRequestClose={() => {
            setIsCloseRequested(true);
            isInitialRender.current = true;
          }}
          isOpen
          isWizzard
          persistent={isSubmitting}
        >
          <Spacer $px={10} />

          <FormikProvider value={formik}>
            <motion.div className="wm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.1 }}>
              <div className="wm__top h-padding">
                <Poppins className="m-title" px={28}>
                  {slides[step]?.title}
                </Poppins>

                <Spacer $px={20} />
                <Stepper slides={slides} step={step} onChangeSlide={onChangeSlide} />
                <Spacer $px={20} />
                <ModalSubTitle title="ASSESSMENT" value={wizardRecomendedControls?.intent.params.assessmentName!} />
                <Spacer $px={20} />
              </div>

              <div className="wm__content" ref={sliderRef}>
                {slides.map((el, idx) => {
                  const Cmp = el.component;
                  return (
                    <Slide isCurrent={step === idx} slideHeight={SLIDE_HEIGHT}>
                      <Cmp
                        onChangeSlide={onChangeSlide}
                        assessmentId={wizardRecomendedControls?.intent.params.assessmentId!}
                      />
                    </Slide>
                  );
                })}
              </div>
            </motion.div>
            <div className="wm__bottom h-padding">
              <BottomPanel
                btnPrev={
                  step !== 0 && {
                    exe: () => {
                      const slideId = slides[step - 1]?.id;
                      if (slideId) {
                        mpEvent(MPEvents.WizardSlideChanged, {
                          slideId,
                        });
                      }

                      slider.current?.prev();
                    },
                    icon: true,
                    label: 'PREVIOUS',
                  }
                }
                btnNext={{
                  exe: () => {
                    if (isLastSlide) {
                      handleSubmit();
                    } else {
                      const slideId = slides[step + 1]?.id;
                      if (slideId) {
                        mpEvent(MPEvents.WizardSlideChanged, {
                          slideId,
                        });
                      }
                      slider.current?.next();
                    }
                  },
                  icon: !isLastSlide,
                  label: isLastSlide ? 'CONFIRM' : 'NEXT',
                  loading: isLastSlide ? isSubmitting : undefined,
                  disabled:
                    isSubmitting ||
                    (wizardRecomendedControls?.intent.type === WizardRecomendedControlsIntent.linkControls &&
                      isLastSlide &&
                      !hasNewSelected),
                }}
              />
            </div>

            {error && (
              <div className="err-container">
                <Spacer $px={10} />
                <Poppins px={14} color="error">
                  {error.message || 'Server error'}
                </Poppins>
              </div>
            )}
          </FormikProvider>

          <Spacer $px={40} />
        </StyledModal>
      }
    />
  );
};
